/**
 * Percentage-based hotspots on piercing guide illustrations (819×1024 assets).
 * Keys are official sales-reference names (Référence Vente), normalized uppercase.
 * x/y = center of jewelry on the photo; r = radius as % of image width.
 */

export interface GuideHotspotSpot {
  x: number;
  y: number;
  /** Radius as % of image width (default 4.2). */
  r?: number;
}

export interface GuideHotspot {
  spots: GuideHotspotSpot[];
}

function normalizeTypeKey(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/** Official type name → hotspot(s) on that zone’s guide image. */
export const PIERCING_GUIDE_HOTSPOTS_BY_NAME: Record<string, GuideHotspot> = {
  // ——— Oreille ———
  LOBE: { spots: [{ x: 43, y: 74, r: 5 }] },
  'LOBE BARBELL TITANE': { spots: [{ x: 43, y: 74, r: 5 }] },
  'LOBE LABRET': { spots: [{ x: 43, y: 74, r: 5 }] },
  'LOBE BCR2.4': { spots: [{ x: 43, y: 74, r: 5 }] },
  'LOBE BCR 3.2': { spots: [{ x: 43, y: 74, r: 5 }] },
  'LOBE CIRCULAR BARBELL': { spots: [{ x: 43, y: 74, r: 5 }] },
  HELIX: {
    spots: [
      { x: 60, y: 34, r: 3.8 },
      { x: 62, y: 40, r: 3.8 },
    ],
  },
  'HELIX BARBELL TITANE': {
    spots: [
      { x: 60, y: 34, r: 3.8 },
      { x: 62, y: 40, r: 3.8 },
    ],
  },
  'HELIX LABRET': {
    spots: [
      { x: 60, y: 34, r: 3.8 },
      { x: 62, y: 40, r: 3.8 },
    ],
  },
  'HELIX PTFE': {
    spots: [
      { x: 60, y: 34, r: 3.8 },
      { x: 62, y: 40, r: 3.8 },
    ],
  },
  'TRAGUS VERTICAL': { spots: [{ x: 35, y: 55, r: 4 }] },
  'TRAGUS BARBELL TITANE': { spots: [{ x: 35, y: 55, r: 4 }] },
  'TRAGUS BCR': { spots: [{ x: 35, y: 55, r: 4 }] },
  'CONQUE PTFE': { spots: [{ x: 59, y: 57, r: 4.8 }] },
  'CONQUE BARBELL TITANE': { spots: [{ x: 59, y: 57, r: 4.8 }] },
  'CONQUE BCR': { spots: [{ x: 59, y: 57, r: 4.8 }] },
  'DAITH PTFE': { spots: [{ x: 45, y: 47, r: 4.5 }] },
  'DAITH BCR': { spots: [{ x: 45, y: 47, r: 4.5 }] },
  'DAITH PTFE+BARBELL COURBE': { spots: [{ x: 45, y: 47, r: 4.5 }] },
  'DEATH BARB TITANE': { spots: [{ x: 45, y: 47, r: 4.5 }] },
  'ROOK PTFE': { spots: [{ x: 52, y: 36, r: 4 }] },
  'ROOK BCR': { spots: [{ x: 52, y: 36, r: 4 }] },
  'ROOK PTFE+BARBELL COURBE': { spots: [{ x: 52, y: 36, r: 4 }] },
  'ROOK BARB TITANE': { spots: [{ x: 52, y: 36, r: 4 }] },
  'CARTILAGE INTERNE': { spots: [{ x: 59, y: 57, r: 4.8 }] },
  'ANTI-TRAGUS PTFE': { spots: [{ x: 35, y: 55, r: 4 }] },
  'ANTI-TRAGUS BARBELL TITAN': { spots: [{ x: 35, y: 55, r: 4 }] },
  'ANTI-TRAGUS BCR': { spots: [{ x: 35, y: 55, r: 4 }] },
  'ANTI-TRAGUS': { spots: [{ x: 35, y: 55, r: 4 }] },
  'SNUG PTFE': { spots: [{ x: 59, y: 57, r: 4 }] },
  'SNUG BCR': { spots: [{ x: 59, y: 57, r: 4 }] },
  'SNUG BARB TITANE': { spots: [{ x: 59, y: 57, r: 4 }] },
  'INDUSTRIEL PTFE': {
    spots: [
      { x: 60, y: 34, r: 3.8 },
      { x: 52, y: 36, r: 3.8 },
    ],
  },
  'INDUSTRIEL BARBELL TITAN': {
    spots: [
      { x: 60, y: 34, r: 3.8 },
      { x: 52, y: 36, r: 3.8 },
    ],
  },

  // ——— Nez ———
  NARINE: { spots: [{ x: 27, y: 59, r: 4 }] },
  SEPTUM: { spots: [{ x: 37, y: 70, r: 5 }] },
  'SEPTUM OJ': { spots: [{ x: 37, y: 70, r: 5 }] },
  'NARINE OJ': { spots: [{ x: 27, y: 59, r: 4 }] },

  // ——— Nombril ———
  NOMBRIL: { spots: [{ x: 49.5, y: 40, r: 5 }] },
  'NOMBRIL BARB CBE TITANE': { spots: [{ x: 49.5, y: 40, r: 5 }] },
  'NB BANANA STR TITANE': { spots: [{ x: 49.5, y: 40, r: 5 }] },
  'NOMBRIL PTFE+BANANA STRAS': { spots: [{ x: 49.5, y: 40, r: 5 }] },
  'NOMBRIL BCR TITANE': { spots: [{ x: 49.5, y: 40, r: 5 }] },
  'NOMBRIL OR': { spots: [{ x: 49.5, y: 40, r: 5 }] },

  // ——— Sourcil / Arcade / Bridge ———
  ARCADE: {
    spots: [
      { x: 40, y: 33, r: 3.5 },
      { x: 41, y: 39, r: 3.5 },
    ],
  },
  'ARCADE VERTICALE BCR': {
    spots: [
      { x: 40, y: 33, r: 3.5 },
      { x: 41, y: 39, r: 3.5 },
    ],
  },
  'ARCADE VERTICALE CIRCULAR': {
    spots: [
      { x: 40, y: 33, r: 3.5 },
      { x: 41, y: 39, r: 3.5 },
    ],
  },
  'ARCADE OJ': {
    spots: [
      { x: 40, y: 33, r: 3.5 },
      { x: 41, y: 39, r: 3.5 },
    ],
  },
  'ARCADE HORIZONTALE AGRAFE': {
    spots: [
      { x: 58.5, y: 40.5, r: 3.5 },
      { x: 66.5, y: 41, r: 3.5 },
    ],
  },
  BRIDGE: {
    spots: [
      { x: 84, y: 55.5, r: 3.5 },
      { x: 88, y: 55.5, r: 3.5 },
    ],
  },
  'BRIDGE BARBELL TITANE': {
    spots: [
      { x: 84, y: 55.5, r: 3.5 },
      { x: 88, y: 55.5, r: 3.5 },
    ],
  },
  'LARME OU ANTI-ARCADE': {
    spots: [
      { x: 34.5, y: 61, r: 3.5 },
      { x: 35, y: 65.5, r: 3.2 },
    ],
  },

  // ——— Langue / Bouche ———
  LANGUE: { spots: [{ x: 49, y: 41, r: 4.5 }] },
  'LANGUE BARB TITANE': { spots: [{ x: 49, y: 41, r: 4.5 }] },
  'LANGUE 2BARB TITANE': { spots: [{ x: 49, y: 41, r: 4.5 }] },
  FILET: {
    spots: [
      { x: 50, y: 69, r: 3.5 },
      { x: 50, y: 75, r: 3.5 },
    ],
  },
  'FILET OJ': {
    spots: [
      { x: 50, y: 69, r: 3.5 },
      { x: 50, y: 75, r: 3.5 },
    ],
  },

  // ——— Tour de bouche / Lèvre ———
  MEDUSA: { spots: [{ x: 50, y: 40, r: 3.8 }] },
  'MEDUSA LABRET TITANE': { spots: [{ x: 50, y: 40, r: 3.8 }] },
  'MEDUSA 2 PTFE': { spots: [{ x: 50, y: 40, r: 3.8 }] },
  'MEDUSA OJ': { spots: [{ x: 50, y: 40, r: 3.8 }] },
  LEVRE: { spots: [{ x: 50.5, y: 62.5, r: 3.8 }] },
  'LABRET TITANE': { spots: [{ x: 50.5, y: 62.5, r: 3.8 }] },
  '2 LABRETS PTFE': { spots: [{ x: 50.5, y: 62.5, r: 3.8 }] },
  MOUCHE: { spots: [{ x: 68, y: 43.5, r: 3.6 }] },
  'MOUCHE LABRET TITANE': { spots: [{ x: 68, y: 43.5, r: 3.6 }] },
  'MOUCHE 2LABRETS PTFE': { spots: [{ x: 68, y: 43.5, r: 3.6 }] },
  'MOUCHE OJ': { spots: [{ x: 68, y: 43.5, r: 3.6 }] },
  FOSETTE: { spots: [{ x: 50, y: 55, r: 3.8 }] },
  FOSSETTE: { spots: [{ x: 50, y: 55, r: 3.8 }] },
  'FOSSETTE OJ': { spots: [{ x: 50, y: 55, r: 3.8 }] },
  'JOUE LABRET TITANE': { spots: [{ x: 72, y: 50, r: 3.8 }] },
  'JOUE 2 LABRETS PTFE': { spots: [{ x: 72, y: 50, r: 3.8 }] },
};

/** @deprecated Legacy id keys — kept so old bookmarks/tests still resolve when possible. */
export const PIERCING_GUIDE_HOTSPOTS: Record<string, GuideHotspot> = {
  'piercing-ear-lobe': PIERCING_GUIDE_HOTSPOTS_BY_NAME.LOBE,
  'piercing-ear-helix': PIERCING_GUIDE_HOTSPOTS_BY_NAME.HELIX,
  'piercing-nose-nostril': PIERCING_GUIDE_HOTSPOTS_BY_NAME.NARINE,
  'piercing-nose-septum': PIERCING_GUIDE_HOTSPOTS_BY_NAME.SEPTUM,
  'piercing-navel-standard': PIERCING_GUIDE_HOTSPOTS_BY_NAME.NOMBRIL,
  'piercing-lip-medusa': PIERCING_GUIDE_HOTSPOTS_BY_NAME.MEDUSA,
  'piercing-lip-labret': PIERCING_GUIDE_HOTSPOTS_BY_NAME.LEVRE,
  'piercing-tongue-center': PIERCING_GUIDE_HOTSPOTS_BY_NAME.LANGUE,
  'piercing-eyebrow-vertical': PIERCING_GUIDE_HOTSPOTS_BY_NAME.ARCADE,
  'piercing-eyebrow-bridge': PIERCING_GUIDE_HOTSPOTS_BY_NAME.BRIDGE,
};

/**
 * Resolve hotspot by piercing type id (legacy) or sales-reference name (official catalog).
 */
export function getPiercingGuideHotspot(
  piercingTypeIdOrName?: string | null,
): GuideHotspot | null {
  if (!piercingTypeIdOrName) return null;
  if (PIERCING_GUIDE_HOTSPOTS[piercingTypeIdOrName]) {
    return PIERCING_GUIDE_HOTSPOTS[piercingTypeIdOrName];
  }
  const byName = PIERCING_GUIDE_HOTSPOTS_BY_NAME[normalizeTypeKey(piercingTypeIdOrName)];
  return byName ?? null;
}
